import {
  BUILTIN_SHELF_IDS,
  localDateInZone,
  type BuiltinShelfId,
  type RecipeApiItem,
  type ShelfConfig,
  type ShelfFilter,
} from "@recipevault/shared";
import { matchesFilters, type LibraryFilters } from "../recipes/useRecipes.js";

export interface Shelf {
  id: string;
  name: string;
  recipes: RecipeApiItem[];
  builtin: boolean;
}

/** UI cap per shelf on the library home; "See all" (Task 8) escapes it. */
const SHELF_CAP = 12;

type BuiltinShelfDef = {
  name: string;
  predicate: (r: RecipeApiItem) => boolean;
  compare: (a: RecipeApiItem, b: RecipeApiItem) => number;
};

/**
 * Whole-calendar-day number for a YYYY-MM-DD string, anchored at UTC noon
 * (same anchoring as `mondayOf`/`addCalendarDays` in @recipevault/shared, to
 * dodge any DST edge affecting the arithmetic). Only the difference between
 * two such numbers is meaningful.
 *
 * Exported so other day-diff call sites (e.g. InsightsStrip's "last made N
 * days ago") reuse this single source of truth instead of growing a second,
 * possibly-divergent copy of the same UTC-noon-anchor arithmetic.
 */
export function toDayNumber(dateOnly: string): number {
  const [y, m, d] = dateOnly.split("-").map((v) => Number.parseInt(v, 10)) as [number, number, number];
  return Date.UTC(y, m - 1, d, 12) / 86_400_000;
}

/**
 * How many whole days before `today` (household-local YYYY-MM-DD) an ISO
 * timestamp falls. `isoTimestamp` is a UTC instant (recipe `createdAt` /
 * `stats.lastCookedAt`) — it's localized to the household's `tzid` via
 * `localDateInZone` before diffing, same as InsightsStrip's "last made N days
 * ago", so a recipe timestamped just after local midnight in a
 * negative-UTC-offset household doesn't get diffed against the wrong
 * calendar day (guardrail: never UTC-slice a timestamp to get "what day is
 * it" — always localize to the household tz first).
 */
function daysAgo(today: string, isoTimestamp: string, tzid: string): number {
  return toDayNumber(today) - toDayNumber(localDateInZone(tzid, new Date(isoTimestamp)));
}

/**
 * Display names for the 5 fixed builtin shelves — single source of truth.
 * `buildBuiltinShelfDefs` below sources its `name` fields from here, and
 * ManageShelvesSheet (Task 9) consumes this directly for its shelf-list rows
 * (which include hidden/empty shelves that never appear in a derived Shelf[],
 * so it can't just read `.name` off deriveShelves' output).
 */
export const BUILTIN_SHELF_NAMES: Record<BuiltinShelfId, string> = {
  "want-to-try": "Want to try",
  "most-cooked": "Most cooked",
  "recently-added": "Recently added",
  "under-30": "Under 30 minutes",
  "not-in-a-while": "Haven't made in a while",
};

/**
 * Canonical shelf ordering, shared by `deriveShelves` (below) and
 * ManageShelvesSheet (Task 9) so the two never drift: `config.order`'s known
 * ids first (in listed order, first occurrence only) — then unlisted
 * builtins in `BUILTIN_SHELF_IDS` order — then unlisted customs in
 * `config.custom` (creation) order. "Known" = the 5 fixed builtin ids plus
 * every id in `config.custom`; unknown ids in `config.order` (typos, ids of
 * shelves deleted since the order was last written) and duplicate ids are
 * silently dropped.
 *
 * Pure, and returns every known id exactly once with nothing dropped for
 * being hidden or empty — callers apply their own hidden/empty filtering on
 * top of this (deriveShelves drops both; ManageShelvesSheet drops neither,
 * since it lists every shelf so hidden/empty ones stay reachable to manage).
 */
export function orderShelfIds(config: ShelfConfig): string[] {
  const known = new Set<string>([...BUILTIN_SHELF_IDS, ...config.custom.map((c) => c.id)]);
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const id of config.order) {
    if (known.has(id) && !seen.has(id)) {
      ids.push(id);
      seen.add(id);
    }
  }
  for (const id of BUILTIN_SHELF_IDS) {
    if (!seen.has(id)) {
      ids.push(id);
      seen.add(id);
    }
  }
  for (const custom of config.custom) {
    if (!seen.has(custom.id)) {
      ids.push(custom.id);
      seen.add(custom.id);
    }
  }
  return ids;
}

function buildBuiltinShelfDefs(today: string, tzid: string): Record<BuiltinShelfId, BuiltinShelfDef> {
  return {
    "want-to-try": {
      name: BUILTIN_SHELF_NAMES["want-to-try"],
      predicate: (r) => r.wantToTry === true,
      compare: (a, b) => b.createdAt.localeCompare(a.createdAt), // newest first
    },
    "most-cooked": {
      name: BUILTIN_SHELF_NAMES["most-cooked"],
      predicate: (r) => (r.stats?.cookCount ?? 0) > 0,
      compare: (a, b) => (b.stats?.cookCount ?? 0) - (a.stats?.cookCount ?? 0), // count desc
    },
    "recently-added": {
      name: BUILTIN_SHELF_NAMES["recently-added"],
      predicate: (r) => {
        const days = daysAgo(today, r.createdAt, tzid);
        return days >= 0 && days <= 30;
      },
      compare: (a, b) => b.createdAt.localeCompare(a.createdAt), // newest first
    },
    "under-30": {
      name: BUILTIN_SHELF_NAMES["under-30"],
      predicate: (r) => r.totalTimeMin !== null && r.totalTimeMin <= 30,
      // Non-null guaranteed by predicate; nullish coalescing only satisfies types.
      compare: (a, b) => (a.totalTimeMin ?? 0) - (b.totalTimeMin ?? 0), // quickest first
    },
    "not-in-a-while": {
      name: BUILTIN_SHELF_NAMES["not-in-a-while"],
      predicate: (r) => r.stats?.lastCookedAt != null && daysAgo(today, r.stats.lastCookedAt, tzid) > 21,
      // Non-null guaranteed by predicate.
      compare: (a, b) => (a.stats?.lastCookedAt ?? "").localeCompare(b.stats?.lastCookedAt ?? ""), // oldest first
    },
  };
}

/** Maps the taxonomy part of a ShelfFilter onto matchesFilters' LibraryFilters shape. */
function toLibraryFilters(filter: ShelfFilter): LibraryFilters {
  return {
    tagIds: filter.tagIds ?? [],
    categoryIds: filter.categoryIds ?? [],
    cuisineIds: filter.cuisineId ? [filter.cuisineId] : [],
    dietFlags: filter.dietFlags ?? [],
  };
}

/**
 * Custom-shelf membership: the taxonomy part delegates to matchesFilters
 * (shared with the library's own filter bar); maxTotalTimeMin/difficulty are
 * applied as explicit predicates here instead.
 *
 * Invariant: `toLibraryFilters` above must NEVER forward `maxTotalTimeMin` (or
 * `difficulty`, which has no LibraryFilters field at all) into the
 * `matchesFilters` call below — `maxTotalTimeMin` IS a `LibraryFilters` field
 * (Task 10 added it there for the library's own time chip), but
 * `matchesFilters` also applies it internally, so forwarding it here would
 * double-apply the same cap through two different code paths. Harmless today
 * since both paths agree, but silently fragile if either one's semantics
 * drift — keep the explicit predicates below as the single point of truth for
 * these two fields on the shelf-membership path.
 */
function matchesShelfFilter(r: RecipeApiItem, filter: ShelfFilter): boolean {
  if (!matchesFilters(r, toLibraryFilters(filter))) return false;
  if (filter.maxTotalTimeMin !== undefined) {
    if (r.totalTimeMin === null || r.totalTimeMin > filter.maxTotalTimeMin) return false;
  }
  if (filter.difficulty !== undefined && r.difficulty !== filter.difficulty) return false;
  return true;
}

/**
 * Pure derivation of the library home's "smart shelves" from the
 * library-visible recipe set (already filtered for deleted/pending by the
 * caller) plus the household's shelf-config document.
 *
 * `today` is a household-local YYYY-MM-DD string supplied by the caller —
 * this function never reads the clock itself. `tzid` is the household's IANA
 * timezone, used to localize recipe timestamps (createdAt/lastCookedAt, both
 * UTC instants) onto the same calendar-day basis as `today` before diffing —
 * see `daysAgo`.
 */
export function deriveShelves(
  recipes: RecipeApiItem[],
  config: ShelfConfig,
  today: string,
  tzid: string,
): Shelf[] {
  const builtinDefs = buildBuiltinShelfDefs(today, tzid);
  const shelvesById = new Map<string, Shelf>();

  for (const id of BUILTIN_SHELF_IDS) {
    const def = builtinDefs[id];
    const matched = recipes.filter(def.predicate).sort(def.compare).slice(0, SHELF_CAP);
    shelvesById.set(id, { id, name: def.name, recipes: matched, builtin: true });
  }

  for (const custom of config.custom) {
    const matched = recipes
      .filter((r) => matchesShelfFilter(r, custom.filter))
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)) // updatedAt desc
      .slice(0, SHELF_CAP);
    shelvesById.set(custom.id, { id: custom.id, name: custom.name, recipes: matched, builtin: false });
  }

  // Ordering rule lives in orderShelfIds (shared with ManageShelvesSheet).
  // `shelvesById` was populated above for every builtin + every config.custom
  // entry, i.e. exactly orderShelfIds' "known ids" set — so every id it
  // returns resolves via shelvesById.get() below.
  const hidden = new Set(config.hidden);
  const result: Shelf[] = [];
  for (const id of orderShelfIds(config)) {
    if (hidden.has(id)) continue;
    const shelf = shelvesById.get(id);
    if (shelf && shelf.recipes.length > 0) result.push(shelf);
  }
  return result;
}

/**
 * Predicate form of shelf membership — does `r` belong to shelf `shelfId`
 * (builtin or custom)? Powers the library's "See all" escape hatch (Task 8):
 * filtering an already-sorted `RecipeApiItem[]` down by this predicate scopes
 * it to a single shelf's *uncapped* membership while leaving the caller's own
 * sort order intact (unlike `deriveShelves`, this never sorts or caps).
 * Returns false for an unknown/deleted shelf id.
 */
export function matchesShelf(
  r: RecipeApiItem,
  shelfId: string,
  config: ShelfConfig,
  today: string,
  tzid: string,
): boolean {
  if ((BUILTIN_SHELF_IDS as readonly string[]).includes(shelfId)) {
    const def = buildBuiltinShelfDefs(today, tzid)[shelfId as BuiltinShelfId];
    return def.predicate(r);
  }
  const custom = config.custom.find((c) => c.id === shelfId);
  return custom !== undefined && matchesShelfFilter(r, custom.filter);
}
